import { DfkApi } from './dfk.api.service'
import { decodeRecessiveGenesAndNormalize } from '../helpers/degen.helper'
import { calcuateSummonCost } from '../helpers/prices.helper'
import { getMutationClass, getName } from '../helpers/genes.helpers'
import { PascalCase } from '../helpers/format.helpers'
import { elementGene, genderGene, statsGene } from '../constants/dfk'

// Retrieve a list of heroes based on specified search criteria
export const getHeroesBySearchCriteria = async (options, take = 50, skip = 0) => {
    /*
     * auctionType: sale | assisting | wallet
     */
    const {
        auctionType = 'sale',
        network,
        mainClasses = [],
        subClasses = [],
        profession = 'all',
        wallets = [],
    } = options

    console.log(`Retrieving hero listings ${skip + 1} - ${skip + take} from the Tavern`)

    let filter = ''
    let price = ''

    // When search a wallet, include all heroes
    if (options.auctionType === 'wallet') {
        filter = `owner_in: [${wallets.map(wallet => `"${wallet}"`)}]`
    } else {
        // When searching tavern, apply filters
        filter = `${auctionType}Price_not: null
                  network: "${network}"`
        price = `price: ${auctionType}Price`

        // If specified, add main class to filter
        if (mainClasses.length) {
            filter = `${filter}
    			  mainClass_in: [${mainClasses.map(c => c.gene)}]`
        }

        if (subClasses.length) {
            filter = `${filter}
				  subClass_in: [${subClasses.map(c => c.gene)}]`
        }

        // If specified, add profession to filter
        if (profession !== 'all') {
            filter = `${filter}
    			  professionStr: "${profession.toLowerCase()}"`
        }
    }

    let heroes = await getHeroesInternal(filter, 'all', price, take, skip)
    console.log(`${heroes.length} hero listings retrieved from the Tavern`)
    return heroes
}

// Retrieve the specific hero
export const getHeroById = async id => {
    const filter = `id: ${id}`
    return await getHeroesInternal(filter)
}

// Retrieve the specific heroes
export const getHeroByIds = async ids => {
    const filter = `id_in: [${ids}]`
    return await getHeroesInternal(filter, 'all', '', 10, 0)
}

// Retrieve all heroes in the specified wallets
export const getAllHeroesFromWallets = async wallets => {
    const filter = `owner_in: [${wallets.map(wallet => `"${wallet}"`)}]`
    return await getHeroesInternal(filter, 'all', '', 1000, 0)
}

// Retrieve the list of heroes based on the search criteria
const getHeroesInternal = async (filter, propertySet = 'all', customProperties = '', take = 1, skip = 0) => {
    const allProperties = `
	id
	owner{
	  name
	  wallet: owner
	}
	firstName
	lastName
	originRealm
	network
	rarity
	gender
	generation
	mainClass: mainClassStr
	subClass: subClassStr
	level
	profession: professionStr
	fishing
	foraging
	gardening
	mining
	stamina
	summonsRemaining
	maxSummons
	summons
	active1
	active2
	passive1
	passive2
	statBoost1
	statBoost2
	crafting1: statsUnknown1
	crafting2: statsUnknown2
    hasValidCraftingGenes
	element
	strength
	agility
	endurance
	wisdom
	dexterity
	vitality
	intelligence
	luck
	status
	hpFullAt
	mpFullAt
	statGenesRaw: statGenes`

    const basicProperties = `id
	rarity
	gender
	generation
	mainClass
	subClass
	level
	profession`

    // Query to retrieve a single hero based on their id
    const query = `
	{
		heroes(
			first: ${take}
			skip: ${skip}
			where:{
			  ${filter}
			}
		)
		{
			${propertySet === 'basic' ? basicProperties : allProperties}
			${customProperties}
		}
	}`

    const result = await DfkApi.get(query)

    // Add any calculated value that should apply to all heroes
    let heroes = decodeRecessiveGenesAndNormalize(result.heroes)
    heroes = heroes.map(hero => ({
        ...hero,
        displayId: hero.id.length === 13 ? Number(hero.id.slice(1)).toString() : hero.id,
        element: elementGene[hero.element],
        gardening: TenPercent(hero.gardening),
        gender: genderGene[hero.gender],
        fishing: TenPercent(hero.fishing),
        foraging: TenPercent(hero.foraging),
        mining: TenPercent(hero.mining),
        mutationClass: getMutationClass(hero.mainClass),
        name: getName(hero.firstName, hero.lastName, hero.gender),
        profession: PascalCase(hero.profession),
        statBoost1: statsGene[hero.statBoost1],
        statBoost2: statsGene[hero.statBoost2],
        summonCost: calcuateSummonCost(hero),
    }))

    return heroes
}

// This should be converted to a generic SafeDivision function similiar to the decimalSafeAdd in prices.helper
const TenPercent = input => {
    const inputLength = input.toString().length
    const take = inputLength === 1 ? 3 : inputLength + 1
    const tenPercent = input * 0.1
    const str = tenPercent.toString().substring(0, take)
    return str
}
